import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Check, Mail, Phone, Building2, User, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BecomePartnerPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    email: '',
    phone: '',
    businessType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/partner-submissions`, formData);
      toast.success('Thank you! We\'ll be in touch within 24 hours.');
      
      // Also send email notification
      const mailtoLink = `mailto:info@onyx-homesolutions.com?subject=New Partnership Application - ${formData.companyName}&body=Company: ${formData.companyName}%0D%0AContact: ${formData.contactPerson}%0D%0AEmail: ${formData.email}%0D%0APhone: ${formData.phone}%0D%0ABusiness Type: ${formData.businessType}%0D%0A%0D%0AMessage:%0D%0A${formData.message}`;
      window.location.href = mailtoLink;
      
      setFormData({
        companyName: '',
        contactPerson: '',
        email: '',
        phone: '',
        businessType: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    'Exclusive volume pricing and distributor rates',
    'Dedicated account manager for your business',
    'Priority shipping and fulfillment',
    'Marketing support and co-branding opportunities',
    'Technical training and product certifications',
    'Early access to new products and innovations'
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            <button onClick={() => { window.scrollTo(0, 0); navigate('/'); }} className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_elite-lighting/artifacts/27f7yllt_WhatsApp%20Image%202025-10-18%20at%2008.58.35.jpeg" 
                alt="ONYX Home Solutions" 
                className="h-12 lg:h-16 w-auto object-contain"
                style={{ background: 'transparent' }}
              />
            </button>
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => { window.scrollTo(0, 0); navigate('/'); }} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">
                Home
              </button>
              <button onClick={() => { window.scrollTo(0, 0); navigate('/products'); }} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">
                Products
              </button>
              <button onClick={() => { window.scrollTo(0, 0); navigate('/bulk-pricing'); }} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">
                Bulk
              </button>
              <button onClick={() => { window.scrollTo(0, 0); navigate('/guide'); }} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">
                Guides
              </button>
              <button onClick={() => { window.scrollTo(0, 0); navigate('/become-partner'); }} className="text-sm font-medium transition-colors" style={{ color: '#8B6F47' }}>
                Become a Partner
              </button>
              <button onClick={() => { window.scrollTo(0, 0); navigate('/admin/login'); }} className="text-sm font-medium transition-colors" style={{ color: '#8B6F47' }}>
                Login
              </button>
              <button onClick={() => { window.scrollTo(0, 0); navigate('/cart'); }} className="relative p-2 rounded-full hover:bg-gray-100 transition-colors" style={{ color: '#8B6F47' }}>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </button>
            </div>
            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => {
                const menu = document.getElementById('mobile-menu');
                menu.classList.toggle('hidden');
              }}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
        {/* Mobile Menu */}
        <div id="mobile-menu" className="hidden md:hidden border-t border-gray-200 bg-white">
          <div className="px-4 py-3 space-y-1">
            <button onClick={() => { window.scrollTo(0, 0); navigate('/'); }} className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg">
              Home
            </button>
            <button onClick={() => { window.scrollTo(0, 0); navigate('/products'); }} className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg">
              Products
            </button>
            <button onClick={() => { window.scrollTo(0, 0); navigate('/bulk-pricing'); }} className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg">
              Bulk
            </button>
            <button onClick={() => { window.scrollTo(0, 0); navigate('/guide'); }} className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg">
              Guides
            </button>
            <button onClick={() => { window.scrollTo(0, 0); navigate('/become-partner'); }} className="block w-full text-left px-3 py-2 text-sm text-amber-600 hover:bg-gray-50 rounded-lg">
              Become a Partner
            </button>
            <button onClick={() => { window.scrollTo(0, 0); navigate('/admin/login'); }} className="block w-full text-left px-3 py-2 text-sm text-amber-600 hover:bg-gray-50 rounded-lg">
              Login
            </button>
            <button onClick={() => { window.scrollTo(0, 0); navigate('/cart'); }} className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              Cart
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 overflow-hidden" style={{ background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, #D4A574 1px, transparent 0)', backgroundSize: '40px 40px' }}></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 lg:px-8 text-center text-white">
          <div className="inline-block mb-6 px-6 py-2 rounded-full" style={{ background: 'linear-gradient(135deg, #D4A574 0%, #8B6F47 100%)' }}>
            <span className="text-sm font-bold">PARTNERSHIP PROGRAM</span>
          </div>
          <h1 className="text-5xl lg:text-7xl font-bold mb-6">
            Partner with ONYX
          </h1>
          <p className="text-xl lg:text-2xl text-white/80 max-w-3xl mx-auto leading-relaxed">
            Join our network of trusted contractors, electricians, and distributors. Grow your business with premium products and dedicated support.
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Benefits */}
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold mb-6" style={{ color: '#1d1d1f' }}>
                Partnership Benefits
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                When you partner with ONYX Home Solutions, you gain access to exclusive benefits designed to help your business thrive.
              </p>
              
              <div className="space-y-4 mb-12">
                {benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-start gap-4 p-4 rounded-xl bg-amber-50 border border-amber-200">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #D4A574 0%, #8B6F47 100%)' }}>
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-gray-700 leading-relaxed">{benefit}</p>
                  </div>
                ))}
              </div>

              <div className="p-6 rounded-2xl" style={{ background: 'linear-gradient(135deg, #D4A574 0%, #8B6F47 100%)' }}>
                <h3 className="text-xl font-bold text-white mb-2">Ready to Get Started?</h3>
                <p className="text-white/90">Fill out the form and our partnership team will contact you within 24 hours.</p>
              </div>
            </div>

            {/* Form */}
            <div className="bg-white rounded-3xl shadow-xl p-8 lg:p-10 border border-gray-200">
              <h3 className="text-2xl font-bold mb-6" style={{ color: '#1d1d1f' }}>Partnership Application</h3>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Company Name */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Company Name *
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input 
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      required
                      className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none"
                      placeholder="Your Company LLC"
                    />
                  </div>
                </div>

                {/* Contact Person */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Contact Person *
                  </label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input 
                      type="text"
                      name="contactPerson"
                      value={formData.contactPerson}
                      onChange={handleChange}
                      required
                      className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none"
                      placeholder="John Smith"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input 
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none"
                      placeholder="john@company.com"
                    />
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input 
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>

                {/* Business Type */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Business Type *
                  </label>
                  <select 
                    name="businessType"
                    value={formData.businessType}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none"
                  >
                    <option value="">Select business type</option>
                    <option value="contractor">General Contractor</option>
                    <option value="electrician">Licensed Electrician</option>
                    <option value="distributor">Distributor/Wholesaler</option>
                    <option value="builder">Home Builder</option>
                    <option value="retailer">Retailer</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Tell Us About Your Business
                  </label>
                  <div className="relative">
                    <MessageSquare className="absolute left-4 top-4 w-5 h-5 text-gray-400" />
                    <textarea 
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="4"
                      className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none resize-none"
                      placeholder="Tell us about your business, typical order volume, and how you plan to use ONYX products..."
                    ></textarea>
                  </div>
                </div>

                {/* Submit Button */}
                <Button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full text-white py-4 rounded-full text-lg font-medium"
                  style={{ background: 'linear-gradient(135deg, #D4A574 0%, #8B6F47 100%)' }}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Application'}
                </Button>

                <p className="text-sm text-gray-500 text-center">
                  By submitting this form, you agree to be contacted by our partnership team.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 lg:px-8 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <p className="text-gray-400 text-sm">Premium LED lighting and HVAC solutions for modern homes.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Products</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => { window.scrollTo(0, 0); navigate('/products'); }} className="text-gray-400 hover:text-white">ONYX Lighting</button></li>
                <li><button onClick={() => { window.scrollTo(0, 0); navigate('/products'); }} className="text-gray-400 hover:text-white">ONYX HVAC</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => { window.scrollTo(0, 0); navigate('/'); }} className="text-gray-400 hover:text-white">About Us</button></li>
                <li><button onClick={() => { window.scrollTo(0, 0); navigate('/become-partner'); }} className="text-gray-400 hover:text-white">Become a Partner</button></li>
                <li><button onClick={() => { window.scrollTo(0, 0); navigate('/guide'); }} className="text-gray-400 hover:text-white">Guides</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="mailto:info@onyx-homesolutions.com" className="text-gray-400 hover:text-white">info@onyx-homesolutions.com</a></li>
                <li><a href="tel:519-996-2866" className="text-gray-400 hover:text-white">519-996-2866</a></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-gray-800 text-center text-sm text-gray-400">
            <p>© 2025 ONYX Home Solutions. All rights reserved. | Powered by Syntrexe</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BecomePartnerPage;
